import type { RestoreDestination } from './sessionRestoreUtils';
import { action, makeObservable, observable, reaction } from 'mobx';
import type ft from 'firebase';

import { FirestoreCard } from '@creative-kit/shared';
import { db as firestoreDb } from 'firebase-modules';
import type { RootStore } from '.';
import { Card } from './card';
import { normalizeCardDates } from './cardDateNormalization';
import { db } from './data';

export class DeletedCardsStore {
  private stopListeningToDeletedCards = () => {};
  private rootStore: RootStore;
  deletedCards: Record<string, Card> = {};

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      deletedCards: observable,
      updateDeletedCards: action.bound,
      resetDeletedCards: action,
    });

    this.rootStore = rootStore;
  }

  init() {
    reaction(() => this.rootStore.sessionsStore.currentSession, this.watchDeletedCards, {
      fireImmediately: true,
    });
  }

  watchDeletedCards = () => {
    const { currentSession } = this.rootStore.sessionsStore;
    this.stopListeningToDeletedCards();
    this.resetDeletedCards();
    if (currentSession) {
      // Since we can't efficiently query for cards with deletedAt field using where clause,
      // we'll listen to all cards and filter in memory
      this.stopListeningToDeletedCards = db
        .sessionCards(currentSession.id)
        .onSnapshot(this.updateDeletedCards.bind(this, currentSession.id), (error) => {
          // eslint-disable-next-line no-console
          console.error(
            '[DeletedCardsStore] DeletedCards listener error:',
            error.code,
            error.message,
          );
        });
    }
  };

  resetDeletedCards() {
    for (const key of Object.keys(this.deletedCards)) {
      delete this.deletedCards[key];
    }
  }

  updateDeletedCards(sessionId: string, snapshot: ft.firestore.QuerySnapshot<FirestoreCard>) {
    snapshot.docChanges().forEach((change) => {
      const changeType = change.type;
      const changeDoc = change.doc;
      const cardData = normalizeCardDates(changeDoc.data());

      if (changeType === 'added') {
        // Only add cards that are deleted
        if (cardData.deletedAt) {
          this.deletedCards[changeDoc.id] = new Card(
            changeDoc.id,
            sessionId,
            cardData,
            this.rootStore,
          );
        }
      } else if (change.type === 'modified') {
        if (cardData.deletedAt) {
          // Card was deleted or updated while deleted
          if (this.deletedCards[changeDoc.id]) {
            this.deletedCards[changeDoc.id].updateData(cardData);
          } else {
            // Card was newly deleted
            this.deletedCards[changeDoc.id] = new Card(
              changeDoc.id,
              sessionId,
              cardData,
              this.rootStore,
            );
          }
        } else {
          // Card was restored, remove from deleted cards
          delete this.deletedCards[changeDoc.id];
        }
      } else {
        delete this.deletedCards[changeDoc.id];
      }
    });
  }

  restoreCard = async (cardId: string, destination?: RestoreDestination) => {
    // Delegate to the session card actions restore method
    if (destination) {
      return this.rootStore.sessionCardActions.restoreCardToDestination(cardId, destination);
    }
    return this.rootStore.sessionCardActions.restoreCard(cardId);
  };

  permanentlyDeleteCard = async (cardId: string) => {
    const card = this.deletedCards[cardId];
    if (!card) {
      console.warn(`Attempted to permanently delete non-existent card: ${cardId}`);
      return;
    }

    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) {
      console.error('No current session available for permanent deletion');
      return;
    }

    try {
      // Use transaction to ensure atomicity between document deletion and session update
      await firestoreDb.runTransaction(async (transaction) => {
        // Delete the card document
        const cardRef = currentSession.cardListRef.doc(cardId);
        transaction.delete(cardRef);

        // Remove any lingering references from session.cardIds
        // This is crucial to prevent crashes from orphaned references
        const currentCardIds = currentSession.data.cardIds || [];
        const updatedCardIds = currentCardIds.filter((id) => id !== cardId);

        // Only update if there's a change to avoid unnecessary writes
        if (currentCardIds.length !== updatedCardIds.length) {
          transaction.set(currentSession.sessionRef, { cardIds: updatedCardIds }, { merge: true });
        }
      });

      // Clean up local state after successful deletion
      delete this.deletedCards[cardId];

      // Remove from UI selections to prevent selection of non-existent cards
      this.rootStore.uiActiveSessionStore.deselectCard(cardId);

      // Run a health check to ensure session integrity
      setTimeout(() => {
        this.rootStore.sessionIntegrityUtil.runHealthCheck();
      }, 100);

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Card #${card.cardNumber} permanently deleted`,
      });
    } catch (error) {
      console.error('Failed to permanently delete card:', error);
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Failed to permanently delete card #${card.cardNumber}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      });
    }
  };

  permanentlyDeleteSelected = async (selectedCardIds: string[]) => {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession || selectedCardIds.length === 0) return;

    // Filter out cards that don't exist in deleted cards store
    const cardsToDelete = selectedCardIds
      .map((id) => this.deletedCards[id])
      .filter((card) => card !== undefined);

    const cardIdsToDelete = cardsToDelete.map((card) => card.id);

    if (cardIdsToDelete.length === 0) {
      console.warn('No valid cards found for permanent deletion');
      return;
    }

    try {
      // Use transaction to ensure atomicity for all operations
      await firestoreDb.runTransaction(async (transaction) => {
        // Delete all card documents
        cardIdsToDelete.forEach((cardId) => {
          const cardRef = currentSession.cardListRef.doc(cardId);
          transaction.delete(cardRef);
        });

        // Remove all card IDs from session.cardIds in one operation
        const currentCardIds = currentSession.data.cardIds || [];
        const updatedCardIds = currentCardIds.filter((id) => !cardIdsToDelete.includes(id));

        // Only update if there are changes
        if (currentCardIds.length !== updatedCardIds.length) {
          transaction.set(currentSession.sessionRef, { cardIds: updatedCardIds }, { merge: true });
        }
      });

      // Clean up local state after successful deletion
      cardIdsToDelete.forEach((cardId) => {
        delete this.deletedCards[cardId];
      });

      // Clear UI selections for deleted cards
      this.rootStore.uiActiveSessionStore.deselectCards(cardIdsToDelete);

      // Run a health check to ensure session integrity after bulk deletion
      setTimeout(() => {
        this.rootStore.sessionIntegrityUtil.runHealthCheck();
      }, 100);

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `${cardsToDelete.length} cards permanently deleted`,
      });
    } catch (error) {
      console.error('Failed to permanently delete selected cards:', error);
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Failed to permanently delete selected cards: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      });
    }
  };

  /**
   * Permanently delete active (non-deleted) cards by first soft-deleting them
   * then immediately permanently deleting them. This is used for "Delete Permanently"
   * actions that bypass the trash.
   */
  permanentlyDeleteActiveCards = async (cardIds: string[]) => {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession || cardIds.length === 0) return;

    // Filter to only include cards that actually exist and aren't already deleted
    const activeCards = cardIds
      .map((id) => this.rootStore.cardsStore.cards[id])
      .filter((card) => card !== undefined && !card.data.deletedAt);

    if (activeCards.length === 0) {
      console.warn('No valid active cards found for permanent deletion');
      return;
    }

    const cardIdsToDelete = activeCards.map((card) => card.id);

    try {
      // Use transaction to permanently delete active cards and update session
      await firestoreDb.runTransaction(async (transaction) => {
        // Delete the card documents permanently
        cardIdsToDelete.forEach((cardId) => {
          const cardRef = currentSession.cardListRef.doc(cardId);
          transaction.delete(cardRef);
        });

        // Remove from session.cardIds array
        const currentCardIds = currentSession.data.cardIds || [];
        const updatedCardIds = currentCardIds.filter((id) => !cardIdsToDelete.includes(id));

        if (currentCardIds.length !== updatedCardIds.length) {
          transaction.set(currentSession.sessionRef, { cardIds: updatedCardIds }, { merge: true });
        }
      });

      // Clear UI selections for deleted cards
      this.rootStore.uiActiveSessionStore.deselectCards(cardIdsToDelete);

      // Run a health check to ensure session integrity
      setTimeout(() => {
        this.rootStore.sessionIntegrityUtil.runHealthCheck();
      }, 100);

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `${activeCards.length} cards permanently deleted`,
      });
    } catch (error) {
      console.error('Failed to permanently delete active cards:', error);
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Failed to permanently delete cards: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      });
    }
  };

  get deletedCardsList() {
    return Object.values(this.deletedCards).sort(
      (a, b) => (b.data.deletedAt?.getTime() || 0) - (a.data.deletedAt?.getTime() || 0),
    );
  }
}
